// src/services/permissionService.js
import apiClient from './api';

export const permissionService = {

  // Existing methods
  getAvailableRoles: async () => {
    try {
      const response = await apiClient.get('/users/groups/');
      return {
        success: true,
        data: response.data || []
      };
    } catch (error) {
      console.error('Error fetching available roles:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch roles'
      };
    }
  },

  getRoutes: async () => {
    try {
      const response = await apiClient.get('/permissions/routes/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching routes:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to fetch routes'
      };
    }
  },

  getCardPermissions: async () => {
    try {
      const response = await apiClient.get('/permissions/dashboard-cards/');
      const data = response.data?.results || response.data || [];
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      console.error('Error fetching card permissions:', error);
      return {
          success: false,
          error: error.response?.data?.detail || error.message || 'Failed to fetch card permissions',
          data: []
      };
    }
  },

  updateCardPermissionRoles: async (cardIdentifier, roles) => {
    if (!cardIdentifier || !Array.isArray(roles)) {
         console.error("Invalid arguments for updateCardPermissionRoles");
         return { success: false, error: "Invalid arguments provided." };
    }
    try {
      const response = await apiClient.put(`/permissions/dashboard-cards/${cardIdentifier}/roles/`, { roles });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      console.error(`Error updating roles for card ${cardIdentifier}:`, error);
      return {
          success: false,
          error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to update card roles'
      };
    }
  },

  // New enhanced permission methods

  /**
   * Get comprehensive permission summary for a user
   * @param {number} userId - Optional user ID (defaults to current user)
   */
  getUserPermissionsSummary: async (userId = null) => {
    try {
      const url = userId 
        ? `/permissions/user-summary/${userId}/` 
        : '/permissions/user-summary/';
      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error fetching user permissions summary:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch permissions summary'
      };
    }
  },

  /**
   * Get customer access levels
   */
  getCustomerAccessLevels: async () => {
    try {
      const response = await apiClient.get('/permissions/customer-access/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching customer access levels:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch customer access levels'
      };
    }
  },

  /**
   * Update customer access level by group ID
   * @param {number} groupId - Group ID
   * @param {string} accessType - Access type (all, assigned, team, own, none)
   * @param {string[]} teams - Team names for team-based access
   */
  updateGroupCustomerAccess: async (groupId, accessType, teams = []) => {
    try {
      const response = await apiClient.put(`/permissions/customer-access/${groupId}/`, {
        access_type: accessType,
        teams: teams
      });
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error updating group customer access:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update group customer access'
      };
    }
  },

  getLeadAccessLevels: async () => {
    try {
      const response = await apiClient.get('/permissions/lead-access/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching lead access levels:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch lead access levels'
      };
    }
  },

  updateGroupLeadAccess: async (groupId, accessType) => {
    try {
      const response = await apiClient.put(`/permissions/lead-access/${groupId}/`, {
        access_type: accessType
      });
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error updating group lead access:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update group lead access'
      };
    }
  },

  getBankAccountAccessLevels: async () => {
    try {
      const response = await apiClient.get('/permissions/bank-account-access/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching bank account access levels:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch bank account access levels'
      };
    }
  },

  getAvailableCashAccounts: async () => {
    try {
      const response = await apiClient.get('/permissions/bank-account-access/accounts/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message
      };
    }
  },

  updateGroupBankAccountAccess: async (groupId, accessType, allowedAccountIds = null) => {
    try {
      const payload = { access_type: accessType };
      if (allowedAccountIds !== null) {
        payload.allowed_account_ids = allowedAccountIds;
      }
      const response = await apiClient.put(`/permissions/bank-account-access/${groupId}/`, payload);
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error updating group bank account access:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update group bank account access'
      };
    }
  },

  /**
   * Get customer assignments
   * @param {Object} filters - Optional filters (customer_id, user_id)
   */
  getCustomerAssignments: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(`/permissions/customer-assignments/?${params}`);
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching customer assignments:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch customer assignments'
      };
    }
  },

  /**
   * Check specific permission
   * @param {string} permission - Permission string (e.g., 'orders.view_order')
   * @param {Object} context - Optional context (model, object_id, user_id)
   */
  checkPermission: async (permission, context = {}) => {
    try {
      const response = await apiClient.post('/permissions/check-permission/', {
        permission,
        ...context
      });
      return {
        success: true,
        hasPermission: response.data?.data?.has_permission || false,
        data: response.data?.data || {}
      };
    } catch (error) {
      console.error('Error checking permission:', error);
      return {
        success: false,
        hasPermission: false,
        error: error.response?.data?.message || error.message || 'Failed to check permission'
      };
    }
  },

  /**
   * Get permission audit logs (superuser only)
   * @param {Object} filters - Optional filters (user_id, action, days)
   */
  getAuditLogs: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(`/permissions/audit-logs/?${params}`);
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch audit logs'
      };
    }
  },

  // Helper methods for common permission checks

  /**
   * Check if user can perform action on a model
   * @param {string} model - Model name (e.g., 'orders.order')
   * @param {string} action - Action (view, add, change, delete)
   * @param {number} objectId - Optional object ID for object-level check
   */
  canPerformAction: async (model, action, objectId = null) => {
    const permission = `${model.split('.')[0]}.${action}_${model.split('.')[1]}`;
    const context = objectId ? { model, object_id: objectId } : {};
    return await permissionService.checkPermission(permission, context);
  },

  /**
   * Check customer access
   * @param {number} customerId - Customer ID
   * @param {string} action - Action (view, edit, create_order, etc.)
   */
  checkCustomerAccess: async (customerId, action = 'view') => {
    return await permissionService.checkPermission(
      `customers.${action}_customer`,
      { model: 'customers.customer', object_id: customerId }
    );
  },

  // Permission Matrix methods

  /**
   * Get all groups
   * @returns {Promise<Object>} Result with success status and groups data
   */
  getGroups: async () => {
    try {
      const response = await apiClient.get('/permissions/groups/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching groups:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch groups'
      };
    }
  },

  // Roles Access methods

  // Customer Access Assignment methods

  /**
   * Get users in a specific group
   * @param {number} groupId - Group ID
   */
  getUsersInGroup: async (groupId) => {
    try {
      const response = await apiClient.get(`/permissions/customer-access/groups/${groupId}/users/`);
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error(`Error fetching users in group ${groupId}:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch users in group'
      };
    }
  },

  // Analytics endpoints

  /**
   * Get permission system metrics
   */
  getPermissionMetrics: async () => {
    try {
      const response = await apiClient.get('/permissions/analytics/metrics/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching permission metrics:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch permission metrics'
      };
    }
  },

  /**
   * Get permission usage statistics
   * @param {number} days - Number of days to analyze (default: 30)
   */
  getPermissionUsageStats: async (days = 30) => {
    try {
      const response = await apiClient.get(`/permissions/analytics/usage/?days=${days}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching permission usage stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch permission usage stats'
      };
    }
  },

  /**
   * Get access distribution analysis
   */
  getAccessDistribution: async () => {
    try {
      const response = await apiClient.get('/permissions/analytics/distribution/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching access distribution:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch access distribution'
      };
    }
  },

  /**
   * Get permission anomalies
   */
  getPermissionAnomalies: async () => {
    try {
      const response = await apiClient.get('/permissions/analytics/anomalies/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching permission anomalies:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch permission anomalies'
      };
    }
  },

  /**
   * Get permission health score
   */
  getPermissionHealth: async () => {
    try {
      const response = await apiClient.get('/permissions/analytics/health/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching permission health:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch permission health'
      };
    }
  },

  // Dashboard Cards Management

  /**
   * Auto-create all dashboard cards from frontend definitions
   * Creates missing cards in backend database with default roles
   * @returns {Promise<Object>} Result with success status and creation summary
   */
  autoCreateDashboardCards: async () => {
    try {
      const response = await apiClient.post('/permissions/dashboard-cards/auto-create/');
      return {
        success: true,
        data: response.data?.data || response.data || {},
        message: response.data?.message || 'Cards auto-created successfully'
      };
    } catch (error) {
      console.error('Error auto-creating dashboard cards:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to auto-create dashboard cards'
      };
    }
  },

  /**
   * Get all role page visibility settings
   * @returns {Promise<Object>} Result with success status and visibility data
   */
  getRolePageVisibility: async () => {
    try {
      const response = await apiClient.get('/permissions/role-page-visibility/');
      return {
        success: true,
        data: response.data?.data || response.data || []
      };
    } catch (error) {
      console.error('Error fetching role page visibility:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch role page visibility'
      };
    }
  },

  /**
   * Update role page visibility settings for a group
   * @param {Object} visibilityData - Visibility settings data
   * @param {string} visibilityData.group - Group name
   * @param {boolean} [visibilityData.visible_in_map] - Show in map filters
   * @param {boolean} [visibilityData.visible_in_shop_assignment] - Show in shop assignment
   * @param {boolean} [visibilityData.visible_in_sales_assignment] - Show in sales assignment
   * @returns {Promise<Object>} Result with success status and updated data
   */
  updateRolePageVisibility: async (visibilityData) => {
    try {
      const response = await apiClient.post('/permissions/role-page-visibility/', visibilityData);
      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error updating role page visibility:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to update role page visibility'
      };
    }
  },

  // PermissionSet methods

};

export default permissionService;